import { unique } from '@/shared/lib/array';
import { cacheService } from '@/shared/lib/cache';
import { cutString } from '@/shared/lib/string';
import { RackMaterial, ShelfDeck } from '@/shared/types/configurator';
import {
	CACHE_KEY,
	CACHE_TIME,
	CACHE_VERSION,
	RAL_DECK_LABEL,
	TABLE_KEYS,
	ZINC_DECK_LABEL,
} from '../constants';
import {
	CacheData,
	ConfiguratorResultData,
	GoogleTableResponse,
	isValidValueType,
	TableKey,
} from '../types';

export class ConfiguratorData {
	constructor(private readonly tableId: string) {}

	async getData() {
		const cachedData = cacheService.get<CacheData>(CACHE_KEY);
		if (cachedData && cachedData?.version === CACHE_VERSION) {
			return cachedData.data;
		}

		const response = await this.getResponse();
		const parsedResponse = this.parseResponse(response);

		cacheService.set(CACHE_KEY, parsedResponse, CACHE_TIME);

		return parsedResponse;
	}

	private async getResponse() {
		const url = this.getFetchUrl();

		const response = await fetch(url);
		const result = await response.text();
		const parsedResult: GoogleTableResponse = JSON.parse(
			cutString(result, { start: 47, end: 2 }),
		);

		return parsedResult;
	}

	private parseResponse(response: GoogleTableResponse) {
		const keysIndexes: Partial<Record<TableKey, number>> = {};

		for (let i = 0; i < response.table.cols.length; i++) {
			const columnOptions = response.table.cols[i];

			if (TABLE_KEYS[columnOptions.label]) {
				keysIndexes[TABLE_KEYS[columnOptions.label]] = i;
			}
		}

		const result: ConfiguratorResultData = {
			heights: {
				[RackMaterial.RAL_2004]: [],
				[RackMaterial.ZN]: [],
			},
			widths: [],
			depths: [],
			prices: {
				beams: {},
				shelves: {},
				connector: 0,
			},
		};

		for (const row of response.table.rows) {
			const rawHeightValue = row?.c?.[keysIndexes[TableKey.HEIGHT]]?.v;
			const rawWidthValue = row?.c?.[keysIndexes[TableKey.WIDTH]]?.v;
			const rawDepthValue = row?.c?.[keysIndexes[TableKey.DEPTH]]?.v;
			const rawPriceValue = row?.c?.[keysIndexes[TableKey.PRICE]]?.v;
			const rawRackValue = row?.c?.[keysIndexes[TableKey.RACK]]?.v;
			const rawDeckValue = row?.c?.[keysIndexes[TableKey.DECK]]?.v;

			const rack = this.parseRack(rawRackValue as string);
			const deck = this.parseDeck(rawDeckValue as string, rack);
			const isConnector = this.getIsConnector(rawDeckValue as string);

			if (isConnector) {
				result.prices.connector = this.parseValue(rawPriceValue);
				continue;
			}

			if (isValidValueType(rawWidthValue)) {
				result.widths.push(this.parseValue(rawWidthValue) * 10);
			}

			if (isValidValueType(rawDepthValue)) {
				result.depths.push(this.parseValue(rawDepthValue) * 10);
			}

			if (isValidValueType(rawHeightValue) && !isValidValueType(rawWidthValue) && rack) {
				result.heights[rack].push(this.parseValue(rawHeightValue) * 10);
			}

			if (
				isValidValueType(rawHeightValue) &&
				isValidValueType(rawDepthValue) &&
				isValidValueType(rawPriceValue) &&
				!isValidValueType(rawWidthValue) &&
				rack
			) {
				const height = this.parseValue(rawHeightValue) * 10;
				const depth = this.parseValue(rawDepthValue) * 10;
				const key = `${rack}_${height}_${depth}`;

				result.prices.beams[key] = this.parseValue(rawPriceValue);
			}

			if (
				!isValidValueType(rawHeightValue) &&
				isValidValueType(rawDepthValue) &&
				isValidValueType(rawPriceValue) &&
				isValidValueType(rawWidthValue) &&
				deck
			) {
				const width = this.parseValue(rawWidthValue) * 10;
				const depth = this.parseValue(rawDepthValue) * 10;
				const key = `${deck}_${width}_${depth}`;

				result.prices.shelves[key] = this.parseValue(rawPriceValue);
			}
		}

		result.heights[RackMaterial.RAL_2004] = unique(result.heights[RackMaterial.RAL_2004]);
		result.heights[RackMaterial.ZN] = unique(result.heights[RackMaterial.ZN]);
		result.widths = unique(result.widths);
		result.depths = unique(result.depths);

		return result;
	}

	private getFetchUrl() {
		return `https://docs.google.com/spreadsheets/d/${this.tableId}/gviz/tq?tqx=out:json`;
	}

	private parseValue(value: string | number) {
		if (typeof value === 'number') {
			return value;
		}

		if (value.includes('cm')) {
			value = value.replace('cm', '');
		}

		return +value;
	}

	private parseRack(value: string) {
		if (value === ZINC_DECK_LABEL) return RackMaterial.ZN;
		if (value === RAL_DECK_LABEL) return RackMaterial.RAL_2004;

		return null;
	}

	private parseDeck(value: string, rack: RackMaterial) {
		if (!value || !rack) return null;

		if (rack === RackMaterial.ZN && value.includes('ДСП'))
			return ShelfDeck.SANDED_PARTICLE_BOARD;
		if (rack === RackMaterial.ZN && value.includes('модульна'))
			return ShelfDeck.GALVANIZED_METAL;

		if (rack === RackMaterial.RAL_2004 && value.includes('ЛДСП'))
			return ShelfDeck.LAMINATED_PARTICLE_BOARD;
		if (rack === RackMaterial.RAL_2004 && value.includes('модульна'))
			return ShelfDeck.PAINTED_METAL;

		return null;
	}

	private getIsConnector(value: string) {
		return value.toLowerCase().includes("з'єднувач");
	}
}
